'use client'

import NumberInput from '@/components/design-system/number-input'
import Select from '@/components/design-system/select'
import TextInput from '@/components/design-system/text-input'
import { originals } from '@/constants/originals'
import updateSearchParams from '@/utils/update-search-params'
import { useTranslations } from 'next-intl'
import { useSearchParams } from 'next/navigation'
import { Suspense } from 'react'
import { useCallback, useRef, useState } from 'react'
import './fairness-verify.scss'
import FairnessVerifyResult from './fariness-verify-result'

const games = originals
  .filter((game) => game.id !== 'coin-flip')
  .map((game) => ({
    label: game.name,
    value: game.id,
  }))
  .sort((a, b) => a.label.localeCompare(b.label))

function FairnessVerifyContent({
  showCalculationBreakdown,
}: {
  showCalculationBreakdown?: boolean
}) {
  const searchParams = useSearchParams()
  const game = searchParams.get('game') || undefined
  const [isLoading, setIsLoading] = useState(false)
  const valuesRef = useRef<Record<string, any>>({})
  const timeoutRef = useRef(0)

  const handleChange = useCallback((name: string, value: any) => {
    valuesRef.current[name] = value

    window.clearTimeout(timeoutRef.current)

    setIsLoading(true)

    timeoutRef.current = window.setTimeout(() => {
      updateSearchParams(valuesRef.current, true)
      setIsLoading(false)
    }, 300)
  }, [])

  const t = useTranslations('fairness-modal.verify-content')

  const gameInputs: Record<string, JSX.Element> = {
    mines: (
      <>
        <Select
          size='lg'
          label='Mines'
          options={Array.from({ length: 24 }, (_, i) => ({
            label: String(i + 1),
            value: String(i + 1),
          }))}
          defaultValue={searchParams.get('minesCount') || '3'}
          onChange={(value) => {
            handleChange('minesCount', value)
          }}
        />
      </>
    ),
    plinko: (
      <>
        <Select
          size='lg'
          label='Risk'
          options={[
            { label: 'Low', value: 'low' },
            { label: 'Medium', value: 'medium' },
            { label: 'High', value: 'high' },
          ]}
          defaultValue={searchParams.get('plinkoRisk') || 'medium'}
          onChange={(value) => {
            handleChange('plinkoRisk', value)
          }}
        />

        <Select
          label='Rows'
          options={Array.from({ length: 9 }, (_, i) => ({
            label: String(i + 8),
            value: String(i + 8),
          }))}
          defaultValue={searchParams.get('plinkoRows') || '16'}
          onChange={(value) => {
            handleChange('plinkoRows', value)
          }}
        />
      </>
    ),
    tower: (
      <Select
        size='lg'
        label='Difficulty'
        options={[
          { label: 'Easy', value: 'easy' },
          { label: 'Medium', value: 'medium' },
          { label: 'Hard', value: 'hard' },
          { label: 'Expert', value: 'expert' },
          { label: 'Master', value: 'master' },
        ]}
        defaultValue={searchParams.get('towerDifficulty') || 'medium'}
        onChange={(value) => {
          handleChange('towerDifficulty', value)
        }}
      />
    ),
    wheel: (
      <>
        <Select
          size='lg'
          label='Risk'
          options={[
            { label: 'Low', value: 'low' },
            { label: 'Medium', value: 'medium' },
            { label: 'High', value: 'high' },
          ]}
          defaultValue={searchParams.get('wheelRisk') || 'medium'}
          onChange={(value) => {
            handleChange('wheelRisk', value)
          }}
        />

        <Select
          size='lg'
          label='Segments'
          options={Array.from({ length: 5 }, (_, i) => ({
            label: String((i + 1) * 10),
            value: String((i + 1) * 10),
          }))}
          defaultValue={searchParams.get('wheelSegments') || '30'}
          onChange={(value) => {
            handleChange('wheelSegments', value)
          }}
        />
      </>
    ),
  }

  return (
    <div className='fairness-verify'>
      <div className='fairness-verify__inputs'>
        <Select
          size='lg'
          label={t('game-placeholder')}
          defaultValue={game}
          options={games}
          onChange={(value) => {
            handleChange('game', value)
          }}
        />

        <TextInput
          size='lg'
          label={t('client-seed-placeholder')}
          defaultValue={searchParams.get('clientSeed') || undefined}
          onChange={(event) => {
            handleChange('clientSeed', event.target.value)
          }}
        />

        <div className='fairness-verify__inputs-wrapper'>
          <TextInput
            size='lg'
            label={t('server-seed-placeholder')}
            defaultValue={searchParams.get('serverSeed') || undefined}
            onChange={(event) => {
              handleChange('serverSeed', event.target.value)
            }}
          />

          <NumberInput
            size='lg'
            label={t('nonce-placeholder')}
            defaultValue={Number(searchParams.get('nonce')) || 0}
            min={0}
            onValueChange={(value) => {
              handleChange('nonce', value ?? 0)
            }}
          />
        </div>

        {game && gameInputs[game]}
      </div>

      <div className='fairness-verify__result'>
        <FairnessVerifyResult isLoading={isLoading} />
      </div>
    </div>
  )
}

export default function FairnessVerify({
  showCalculationBreakdown,
}: {
  showCalculationBreakdown?: boolean
}) {
  return (
    <Suspense>
      <FairnessVerifyContent showCalculationBreakdown={showCalculationBreakdown} />
    </Suspense>
  )
}
